import { Button, Form, Input, message, Modal, Select, Spin } from "antd";
import { useContext, useEffect, useState } from "react";
import axios from "../../config/axios";
import { AuthContext } from "../../Contexts/AuthContext";
import dislikeIcon from "./images/dislike.svg";
import likeIcon from "./images/like.svg";
import styles from "./ProductQAInfo.module.scss";

const ProductQAInfo = ({ productId }) => {
    const [loading, setLoading] = useState(true);
    const [questions, setQuestions] = useState([]);
    const [page, setPage] = useState(1);
    const [question, setQuestion] = useState("");
    const [addQLoading, setAddQLoading] = useState(false);
    const [refresh, setRefresh] = useState(null);
    const [moreLoading, setMoreLoading] = useState(false);
    const { isAuthenticated } = useContext(AuthContext);

    useEffect(() => {
        (async () => {
            try {
                if (page !== 1) {
                    setMoreLoading(true);
                }
                const res = await axios.get(`/public/qa`, {
                    withCredentials: true,
                    params: {
                        product: productId,
                        page,
                        limit: 10,
                    },
                });
                if (page === 1) {
                    setQuestions(res.data.results);
                } else {
                    setQuestions((prev) => prev.concat(res.data.results));
                }
            } catch (error) {
                if (page === 1) return;

                message.error("No More Question Found!");
            } finally {
                setLoading(false);
                setMoreLoading(false);
            }
        })();
    }, [productId, page, refresh]);

    const handleAddQuestion = async () => {
        if (!isAuthenticated) {
            message.error("Please Login First");
            return;
        }
        try {
            if (question === "") {
                message.error("Question is required");
                return;
            }
            setAddQLoading(true);
            const payload = {
                product: productId,
                question: question,
            };
            await axios.post("/public/qa", payload, { withCredentials: true });
            message.success("Question Added!");
            setQuestion("");
            setPage(1);
            setRefresh(Math.random());
        } catch (error) {
            message.error("Can't add question right now");
        } finally {
            setAddQLoading(false);
        }
    };

    const handleQuestionLike = async (question) => {
        if (!isAuthenticated) {
            message.error("You need to login first!");
            return;
        }

        // parse question likes from stringified json
        const reviewLikes = question.likes ? JSON.parse(question.likes) : [];

        // check if user has already liked the question
        const isLiked = reviewLikes.includes(question.user.id);

        if (isLiked) {
            message.error("You have already liked this question");
            return;
        }

        // add user id to question likes
        reviewLikes.push(question.user.id);

        // remove user id from question dislikes
        const reviewDisLikes = question.dislikes ? JSON.parse(question.dislikes) : [];
        const filteredDisLikes = reviewDisLikes.filter((id) => id !== question.user.id);

        // stringify question dislikes
        const dislikes = JSON.stringify(filteredDisLikes);

        // stringify question likes
        const likes = JSON.stringify(reviewLikes);

        // send request to server
        try {
            message.loading("Liking question...");
            await axios.patch(`/public/qa/${question.id}`, { likes, dislikes }, { withCredentials: true });
            message.destroy();
            message.success("Question liked successfully!");
            setRefresh(Math.random());
        } catch (error) {
            message.error("Can't like question right now! Try Again!");
        }
    };

    const handleQuestionDislike = async (question) => {
        if (!isAuthenticated) {
            message.error("You need to login first!");
            return;
        }

        // parse question dislikes from stringified json
        const reviewDisLikes = question.dislikes ? JSON.parse(question.dislikes) : [];

        console.log(reviewDisLikes);

        // check if user has already liked the question
        const isDisLiked = reviewDisLikes.includes(question.user.id);

        if (isDisLiked) {
            message.error("You have already disliked this question");
            return;
        }

        // add user id to question dislikes
        reviewDisLikes.push(question.user.id);

        // remove user id from question likes
        const reviewLikes = question.likes ? JSON.parse(question.likes) : [];
        const filteredLikes = reviewLikes.filter((id) => id !== question.user.id);

        // stringify question likes
        const likes = JSON.stringify(filteredLikes);

        // stringify question likes
        const dislikes = JSON.stringify(reviewDisLikes);

        // send request to server
        try {
            message.loading("Disliking question...");
            await axios.patch(`/public/qa/${question.id}`, { likes, dislikes }, { withCredentials: true });
            message.destroy();
            message.success("Question disliked!");
            setRefresh(Math.random());
        } catch (error) {
            message.error("Can't dislike question right now! Try Again!");
        }
    };

    // report abuse modal states
    const [reportAbuseModalVisible, setReportAbuseModalVisible] = useState(false);
    const [reportAbuseModalId, setReportAbuseModalId] = useState(null);

    return (
        <div className={styles.ProductQAInfo}>
            {/* title */}
            <div className={styles.title}>Have a Question?</div>

            {/* post question form and sort selections */}
            <div className={styles.formAndSelect}>
                <div className={styles.form}>
                    <Input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Type your question" />
                    <Button onClick={handleAddQuestion} type="primary" htmlType="submit" loading={addQLoading}>
                        Post Question
                    </Button>
                </div>
                <div className={styles.select + " hide_mobile"}>
                    <label htmlFor="sort">Sort by</label>
                    <Select
                        defaultValue="most_popular"
                        style={{
                            width: 170,
                        }}
                    >
                        <Select.Option value="most_popular">Most popular</Select.Option>
                    </Select>
                </div>
            </div>

            {/* question, answer and author section */}
            <div className={styles.questionAnswerWrapper}>
                {loading ? (
                    <div className={styles.loading}>
                        <Spin />
                    </div>
                ) : (
                    <>
                        {questions.length > 0 ? (
                            <>
                                {questions.map((question, index) => (
                                    <div className={styles.questionAnswer} key={index}>
                                        {/* question */}
                                        <div className={styles.question}>
                                            <div className={styles.questionTitle}> Question: </div>
                                            <div className={styles.questionBody}> {question.question} </div>
                                        </div>

                                        {/* answer */}
                                        <div className={styles.answer}>
                                            <div className={styles.answerTitle}> Answer: </div>
                                            <div className={styles.answerBody}>
                                                <span>{question.answer ?? "Not Answered Yet!"}</span>

                                                <div className={styles.author}>
                                                    {" "}
                                                    Question by: {question.user?.first_name} {question.user?.last_name}
                                                </div>
                                            </div>
                                        </div>

                                        {/* feedback */}
                                        <div className={styles.feedback}>
                                            <div className={styles.feedback_left}>
                                                <img
                                                    src={likeIcon}
                                                    alt="like"
                                                    onClick={() => {
                                                        handleQuestionLike(question);
                                                    }}
                                                />
                                                <div className="mt-1">({JSON.parse(question.likes ?? "[]").length})</div>
                                                <img
                                                    src={dislikeIcon}
                                                    alt="dislike"
                                                    // style={{ marginRight: 5 }}
                                                    className="me-2 mt-2"
                                                    onClick={() => {
                                                        handleQuestionDislike(question);
                                                    }}
                                                />
                                            </div>
                                            <div
                                                className={styles.feedback_right}
                                                onClick={() => {
                                                    setReportAbuseModalVisible(true);
                                                    setReportAbuseModalId(question.id);
                                                }}
                                            >
                                                Report Abuse
                                            </div>
                                            {reportAbuseModalVisible && reportAbuseModalId === question.id && (
                                                <ReportAbuseModal
                                                    visible={reportAbuseModalVisible}
                                                    setVisible={setReportAbuseModalVisible}
                                                    question={question}
                                                    setRefreshX={setRefresh}
                                                />
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </>
                        ) : (
                            <>
                                <h3 style={{ textAlign: "center" }}>No Questions Found!</h3>
                            </>
                        )}
                    </>
                )}
            </div>
            <div
                className={styles.load_more_text}
                onClick={() => {
                    setPage((prev) => prev + 1);
                }}
            >
                {moreLoading ? "Loading..." : "+ Load more questions & answers"}
            </div>
        </div>
    );
};

export default ProductQAInfo;

const ReportAbuseModal = ({ visible, setVisible, question, setRefreshX }) => {
    const [loading, setLoading] = useState(false);

    // handle review report abuse
    const handleQuestionReportAbuse = async (values) => {
        // parse review reports from stringified json
        const reviewReports = question.reports ? JSON.parse(question.reports) : [];

        // check if user already reported this review
        const alreadyReported = reviewReports.find((report) => report.user === question.user.id);

        // if user already reported this review
        if (alreadyReported) {
            message.error("You already reported this question!");
            return;
        }

        // if user didn't report this review
        // add user to review reports
        reviewReports.push({
            user: question.user.id,
            message: values.message,
        });

        // stringify review reports
        const reports = JSON.stringify(reviewReports);

        // update review reports
        try {
            setLoading(true);
            await axios.patch(`/public/qa/${question.id}`, { reports }, { withCredentials: true });
            message.success("Your report is submitted!");
            setRefreshX(Math.random());
        } catch (error) {
            message.error("Can't report abuse right now! Try again later!");
        } finally {
            setLoading(false);
            setVisible(false);
        }
    };

    return (
        <Modal
            open={visible}
            onCancel={() => {
                setVisible(false);
            }}
            centered
            footer={null}
            title="Report Abuse"
        >
            <Form onFinish={handleQuestionReportAbuse} layout="vertical" scrollToFirstError requiredMark={false}>
                <Form.Item
                    name="message"
                    label="Describe Your Message"
                    rules={[
                        {
                            required: true,
                            message: "Please describe your issue!",
                        },
                    ]}
                >
                    <Input.TextArea />
                </Form.Item>

                <Form.Item className="mb-0">
                    <Button type="primary" htmlType="submit" block loading={loading} disabled={loading}>
                        Send
                    </Button>
                </Form.Item>
            </Form>
        </Modal>
    );
};
